import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PHOTOS } from "@/constants/testIds";
import { Camera, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function ProgressPhotos() {
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const r = await api.get("/photos");
    setPhotos(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = note ? { note } : {};
      await api.post("/photos", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      setNote("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/photos/${id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const token = localStorage.getItem("fp_token");
  const BASE = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="obs-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Progress photos</div>
          <h3 className="font-heading text-2xl uppercase mt-1">Visual benchmark</h3>
        </div>
        <Camera className="h-5 w-5 text-blaze" />
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (e.g. Front · 12 weeks)"
          className="flex-1 bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm"
        />
        <input
          ref={fileRef}
          data-testid={PHOTOS.upload}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
          onChange={onUpload}
          className="hidden"
        />
        <button
          data-testid={PHOTOS.uploadBtn}
          onClick={onPick}
          disabled={uploading}
          className="blaze-btn px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload"}
        </button>
      </div>

      <div data-testid={PHOTOS.list} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {photos.length === 0 ? (
          <div className="col-span-full text-zinc-500 text-sm uppercase tracking-widest text-center py-10">
            No photos yet
          </div>
        ) : (
          photos.map((p) => (
            <div key={p.id} className="relative border border-obsidian-600 rounded-sm overflow-hidden group">
              <img
                src={`${BASE}/api/photos/${p.id}/file?auth=${encodeURIComponent(token)}`}
                alt={p.note || "progress"}
                className="w-full h-44 object-cover"
                loading="lazy"
              />
              <button
                onClick={() => onDelete(p.id)}
                className="absolute top-2 right-2 p-1.5 bg-obsidian-900/80 rounded-sm text-zinc-300 hover:text-blaze opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="h-3 w-3" />
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-obsidian-900 to-transparent p-2">
                <div className="text-[10px] text-zinc-300 truncate">{p.note || "—"}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                  {new Date(p.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
